export type RepertoryQuickTerm = {
  label: string;
  query: string;
};

const EN_TO_DE: Record<string, string> = {
  'runny nose': 'schnupfen',
  'sore throat': 'rachen',
  fever: 'fieber',
  headache: 'kopfschmerz',
  head: 'kopf',
  cough: 'husten',
  pain: 'schmerz',
  abdomen: 'bauch',
  stomach: 'magen',
  anxiety: 'angst',
  nausea: 'übelkeit',
  vomiting: 'erbrechen',
  diarrhea: 'durchfall',
  constipation: 'verstopfung',
  sleep: 'schlaf',
  thirst: 'durst',
  burning: 'brennen',
  weakness: 'schwäche',
  chill: 'frösteln',
  throat: 'hals',
  chest: 'brust',
  back: 'rücken',
  skin: 'haut',
  urine: 'urin',
  vertigo: 'schwindel',
  restlessness: 'unruhe',
};

const DE_TO_EN: Record<string, string> = {
  'Geist und Gemüt': 'Mind',
  Allgemeines: 'Generalities',
  Extremitäten: 'Extremities',
  Kopfschmerz: 'Headache',
  Verschlimmerung: 'Aggravation',
  Besserung: 'Amelioration',
  Schwindel: 'Vertigo',
  Schwäche: 'Weakness',
  Frösteln: 'Chill',
  Erbrechen: 'Vomiting',
  Übelkeit: 'Nausea',
  Verstopfung: 'Constipation',
  Durchfall: 'Diarrhea',
  Atmung: 'Respiration',
  Schlaf: 'Sleep',
  Träume: 'Dreams',
  Appetit: 'Appetite',
  Stuhlgang: 'Stool',
  Rachen: 'Throat',
  Brust: 'Chest',
  Rücken: 'Back',
  Husten: 'Cough',
  Fieber: 'Fever',
  Schmerz: 'Pain',
  Schmerzen: 'Pain',
  Angst: 'Anxiety',
  Durst: 'Thirst',
  Brennen: 'Burning',
  Schweiß: 'Perspiration',
  Haut: 'Skin',
  Kopf: 'Head',
  Augen: 'Eyes',
  Ohren: 'Ears',
  Nase: 'Nose',
  Gesicht: 'Face',
  Mund: 'Mouth',
  Magen: 'Stomach',
  Bauch: 'Abdomen',
  Blase: 'Bladder',
  Herz: 'Heart',
  Hals: 'Throat',
  Schnupfen: 'Runny nose',
  'Aufblähung der Blutgefäße': 'Distension of blood vessels',
  Blutgefäße: 'Blood vessels',
  Aufblähung: 'Distension',
  'während des': 'during',
  'während der': 'during',
  'während dem': 'during',
  während: 'during',
  Fiebers: 'Fever',
  bei: 'during',
  nach: 'after',
  vor: 'before',
  und: 'and',
  mit: 'with',
  ohne: 'without',
  im: 'in the',
  am: 'on the',
  zum: 'to the',
  zur: 'to the',
};

const DE_WORDS: Record<string, string> = {
  aufblähung: 'distension',
  blutgefäße: 'blood vessels',
  blutgefäss: 'blood vessels',
  blutgefäß: 'blood vessel',
  während: 'during',
  fiebers: 'fever',
  fieber: 'fever',
  der: 'the',
  die: 'the',
  das: 'the',
  des: 'of the',
  dem: 'the',
  den: 'the',
  bei: 'during',
  nach: 'after',
  vor: 'before',
  und: 'and',
  mit: 'with',
  ohne: 'without',
  in: 'in',
  an: 'on',
  im: 'in the',
  am: 'on the',
  zum: 'to the',
  zur: 'to the',
  von: 'of',
  allgemeines: 'generalities',
  extremitäten: 'extremities',
  genitalien: 'genitalia',
  verschlimmerung: 'aggravation',
  besserung: 'amelioration',
  schlaflosigkeit: 'sleeplessness',
  herzklopfen: 'palpitation',
  reizbarkeit: 'irritability',
  schwangerschaft: 'pregnancy',
  schwellung: 'swelling',
  blutung: 'bleeding',
  schwindel: 'vertigo',
  schwäche: 'weakness',
  müdigkeit: 'fatigue',
  frösteln: 'chill',
  erbrechen: 'vomiting',
  übelkeit: 'nausea',
  verstopfung: 'constipation',
  durchfall: 'diarrhea',
  atmung: 'respiration',
  schlaf: 'sleep',
  träume: 'dreams',
  appetit: 'appetite',
  stuhlgang: 'stool',
  rachen: 'throat',
  brust: 'chest',
  rücken: 'back',
  husten: 'cough',
  schmerz: 'pain',
  schmerzen: 'pain',
  angst: 'anxiety',
  furcht: 'fear',
  unruhe: 'restlessness',
  durst: 'thirst',
  brennen: 'burning',
  schweiß: 'perspiration',
  haut: 'skin',
  kopf: 'head',
  kopfschmerz: 'headache',
  augen: 'eyes',
  auge: 'eye',
  ohren: 'ears',
  ohr: 'ear',
  nase: 'nose',
  gesicht: 'face',
  mund: 'mouth',
  zähne: 'teeth',
  magen: 'stomach',
  bauch: 'abdomen',
  blase: 'bladder',
  nieren: 'kidneys',
  niere: 'kidney',
  herz: 'heart',
  hals: 'throat',
  kind: 'child',
  kinder: 'children',
  morgen: 'morning',
  nacht: 'night',
  abend: 'evening',
  rechts: 'right',
  links: 'left',
  schnupfen: 'runny nose',
};

function applyPhraseTranslations(text: string): string {
  let translated = text;
  const entries = Object.entries(DE_TO_EN).sort((a, b) => b[0].length - a[0].length);

  for (const [german, english] of entries) {
    if (!translated.toLowerCase().includes(german.toLowerCase())) {
      continue;
    }

    const pattern = new RegExp(german.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    translated = translated.replace(pattern, english);
  }

  return translated;
}

function translateWordToken(token: string): string | null {
  const lower = token.toLowerCase();
  if (DE_WORDS[lower]) {
    return DE_WORDS[lower];
  }

  const genitiveMatch = lower.match(/^(.+?)(s|es)$/);
  if (genitiveMatch && DE_WORDS[genitiveMatch[1]]) {
    return DE_WORDS[genitiveMatch[1]];
  }

  return null;
}

function applyWordTranslations(text: string): string {
  return text
    .split(/(\s+)/)
    .map((part) => {
      if (!part.trim()) {
        return part;
      }

      return translateWordToken(part) ?? part;
    })
    .join('');
}

export const GERMAN_REPERTORY_QUICK_TERMS: RepertoryQuickTerm[] = [
  { label: 'Fever', query: 'Fieber' },
  { label: 'Headache', query: 'Kopfschmerz' },
  { label: 'Abdomen', query: 'Bauch' },
  { label: 'Cough', query: 'Husten' },
  { label: 'Anxiety', query: 'Angst' },
  { label: 'Sleep', query: 'Schlaf' },
];

export const ENGLISH_MM_QUICK_TERMS: RepertoryQuickTerm[] = [
  { label: 'Burning thirst', query: 'burning thirst' },
  { label: 'Restless anxiety', query: 'restless anxiety' },
  { label: 'Fever', query: 'fever' },
  { label: 'Headache', query: 'headache' },
  { label: 'Cough', query: 'cough' },
];

export function isGermanRepertory(language?: string | null, abbrev?: string | null): boolean {
  const lang = String(language ?? '').toLowerCase();
  const code = String(abbrev ?? '').toLowerCase();
  return lang === 'de' || code.endsWith('-de') || code === 'kent-de';
}

export function translateSearchSymptomToGerman(symptom: string): string {
  let result = symptom.trim().toLowerCase();
  if (!result) {
    return '';
  }

  const entries = Object.entries(EN_TO_DE).sort((a, b) => b[0].length - a[0].length);
  for (const [english, german] of entries) {
    const pattern = new RegExp(english.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    result = result.replace(pattern, german);
  }

  return result.trim();
}

export function translateGermanDisplayText(text?: string | null): string | null {
  if (!text?.trim()) {
    return null;
  }

  const original = text.trim();
  const translated = applyWordTranslations(applyPhraseTranslations(original)).replace(/\s+/g, ' ').trim();
  return translated.toLowerCase() === original.toLowerCase() ? null : translated;
}

export function translateGermanPath(fullPath?: string | null): string | null {
  if (!fullPath?.trim()) {
    return null;
  }

  const segments = fullPath.split(',').map((segment) => segment.trim()).filter(Boolean);
  const translated = segments
    .map((segment) => translateGermanDisplayText(segment) ?? segment)
    .join(', ');

  return translated.toLowerCase() === fullPath.trim().toLowerCase() ? null : translated;
}
